import { useTheme } from '@emotion/react'
import {
    AppState,
    AuthenticationState,
    CardBreakpointRatios,
    CardItem,
    createCardClickedEvent,
    entitledToAll,
    FixedRatio,
    isPublicationCardItem,
    toLinkWithHint,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { CommonCardProps } from '../../cards/CardItem.Props'
import { TimestampType } from '../../content/CardTimestamp/CardTimestamp'
import {
    applyContainerRatio,
    ResponsiveContainer,
} from '../../content/Picture/responsive'
import { GetVideoQueue } from '../../content/Video/PlayerInterface'
import { isVideoClick } from '../../__helpers/video-card-helpers'
import { FontScales } from '../../__styling/settings/fontScale'
import { ThemeMargins } from '../../__styling/settings/metrics'
import { MediaMode } from '../CardMedia/CardMedia'
import { doesParticularRatioExist } from '../CardMedia/image-helpers'
import { TeaserMode } from '../CardText/CardTeaser'
import { VideoPlaybackRestrictionTypes } from '../HeroVideoCard/HeroVideoCard'
import { KickerMode } from '../Kicker/Kicker'
import { ArrowIcon } from './assets/ArrowIcon'
import {
    StyledCardArrowWrapper,
    StyledCardByline,
    StyledCardFold,
    StyledCardMedia,
    StyledCardMediaAndText,
    StyledCardText,
    StyledLink,
    StyledSNEnt,
} from './SNEntCard.styled'

export interface CardOrientationOptions {
    type: 'Landscape-SNEnt'
    maxBreakpoint?: 'sm' | 'md'
}

export type ShouldVideoPlayInline = boolean
export type InlineVideoPlayerBreakpoints = 'initial' | 'xs' | 'sm' | 'md'
export type InlineVideoBreakpointOptions = Partial<
    Record<InlineVideoPlayerBreakpoints, ShouldVideoPlayInline>
>
export type InlineVideoPlayerMode = boolean

export type CardType = 'large' | 'medium' | 'small'

export interface SNEntCardProps extends CommonCardProps, ResponsiveContainer {
    hasPadding?: boolean
    hasBackground?: boolean
    hasSeparator?: boolean
    mediaMode?: MediaMode
    hideByline?: boolean
    showProfile?: boolean
    teaserMode: TeaserMode
    kickerMode?: KickerMode

    isLarge?: boolean
    verticalSpacing?: keyof ThemeMargins
    fullWidthOnMobile?: boolean
    /**
     * defaults to `4:3`
     */
    fixedRatio?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    smallImage?: boolean
    fontScale?: FontScales
    isSponsoredCollection?: boolean
    disableImageLazyLoad?: boolean

    timestamp?: TimestampType
    cardNumber: number
    innerRef?: React.RefObject<any>
    cardOrientation?: CardOrientationOptions
    cardType?: CardType // Type of card

    adUnitPath?: string
    canPlayVideoInline?: InlineVideoPlayerMode
    restrictedVideoPlaybackMode?: VideoPlaybackRestrictionTypes
    item: MaybeLoaded<CardItem>
    getVideoQueue?: GetVideoQueue
    isInlineRelatedCollection?: boolean
    showVideoPlayButton?: boolean
    overrideRestrictedVideoPlayback?: boolean
    displayPublicationTime?: boolean
    isRegionItem?: boolean
}

export const SNEntCard: React.FC<SNEntCardProps> = (props) => {
    const [isPlaying, setIsPlaying] = useState(false)
    const theme = useTheme()

    function linkClicked(e: React.MouseEvent<HTMLElement>) {
        if (!props.item.loaded) {
            // We don't want the link to do anything
            e.preventDefault()
            return
        }

        if (
            props.canPlayVideoInline &&
            props.fixedRatio &&
            doesParticularRatioExist(props.fixedRatio, '16:9') &&
            isVideoClick(
                e.nativeEvent.target,
                props.canPlayVideoInline ? ['vjs-poster'] : [],
            )
        ) {
            setIsPlaying(true)
            e.preventDefault()
            return
        } else {
            props.onEvent(
                createCardClickedEvent(
                    props.item.result,
                    'InternalPortrait',
                    'Portrait',
                    props.cardNumber,
                    props.isInlineRelatedCollection
                        ? 'InlineRelatedCollection'
                        : undefined,
                ),
            )
        }
    }

    const {
        className,
        hasBackground,
        hasSeparator,
        verticalSpacing,
        isLarge,
        fixedRatio,
        cardOrientation,
        fullWidthOnMobile,
        containerWidthRatios,
        showProfile,
        showVideoPlayButton,
        restrictedVideoPlaybackMode,
        canPlayVideoInline = undefined,
        overrideRestrictedVideoPlayback,
        cardType,
        item,
        hasPadding,
        isRegionItem,
    } = props

    const authentication = useSelector<AppState, AuthenticationState>(
        ({ authentication }) => authentication,
    )
    const isEntitled = entitledToAll(authentication)

    const allowInlineVideoPlayback = overrideRestrictedVideoPlayback
        ? true
        : canPlayRestrictedVideo(restrictedVideoPlaybackMode, isEntitled) ||
          canPlayVideoInline

    const getVideoQueue = !props.item.loaded ? undefined : props.getVideoQueue
    const setKickerState = isPlaying ? 'hidden' : props.kickerMode
    if (item.loaded && isPublicationCardItem(item.result)) {
        const topicName = item.result.primaryTopic.id
        const dataTbItem = {
            'data-tb-region-item': isRegionItem ? '' : undefined,
        }
        return (
            <StyledSNEnt
                ref={props.innerRef}
                className={className}
                hasBackground={hasBackground}
                hasSeparator={hasSeparator}
                isLarge={isLarge}
                cardOrientation={cardOrientation}
                verticalSpacing={verticalSpacing}
                mediaMode={props.mediaMode}
                fullWidthOnMobile={fullWidthOnMobile}
                hasPadding={hasPadding}
                {...dataTbItem}
            >
                <StyledLink
                    to={toLinkWithHint(props.item)}
                    onClick={linkClicked}
                    hasBackground={hasBackground}
                    isLarge={isLarge}
                    cardOrientation={cardOrientation}
                    hasPadding={props.hasPadding}
                    fullWidthOnMobile={fullWidthOnMobile}
                >
                    <StyledCardMediaAndText cardType={props.cardType}>
                        {props.mediaMode !== 'hidden' && (
                            <StyledCardMedia
                                item={{
                                    loaded: true,
                                    result: item.result,
                                }}
                                fixedRatio={fixedRatio}
                                hasBackground={hasBackground}
                                imageWidths={applyContainerRatio(
                                    containerWidthRatios,
                                    {
                                        mobile: '100vw',
                                        tablet: '100vw',
                                        desktop: `${theme.siteMetrics.mainContentWidth}px`,
                                        fallbackWidth:
                                            theme.siteMetrics.mainContentWidth,
                                    },
                                )}
                                onEvent={props.onEvent}
                                disableImageLazyLoad={
                                    props.disableImageLazyLoad
                                }
                                isLarge={isLarge}
                                cardOrientation={cardOrientation}
                                getVideoQueue={getVideoQueue}
                                willPlayVideoInline={allowInlineVideoPlayback}
                                adUnitPath={props.adUnitPath}
                                mediaMode={props.mediaMode}
                                fullWidthOnMobile={fullWidthOnMobile}
                            />
                        )}
                        <StyledCardText
                            item={{
                                loaded: true,
                                result: item.result,
                            }}
                            hasBackground={hasBackground}
                            teaserMode={props.teaserMode}
                            hideByline={props.hideByline}
                            isLiveSportsTopic={false}
                            kickerMode={'hidden'}
                            fontScale={
                                cardType === 'small'
                                    ? 0.83
                                    : props.fontScale || 1.38
                            }
                            onEvent={props.onEvent}
                            hasPadding={props.hasPadding}
                            isLarge={isLarge}
                            timestamp={props.timestamp}
                            cardOrientation={cardOrientation}
                            innerRef={undefined}
                            disableImageLazyLoad={undefined}
                            className={undefined}
                            cardType={cardType}
                        />
                    </StyledCardMediaAndText>
                </StyledLink>
                {!props.hideByline && (
                    <StyledCardByline
                        item={{
                            loaded: true,
                            result: item.result,
                        }}
                        onEvent={props.onEvent}
                        showProfile={showProfile}
                        cardOrientation={cardOrientation}
                        mediaMode={props.mediaMode}
                        isSNEntCard={true}
                        displayPublicationTime={props.displayPublicationTime}
                    />
                )}
                <StyledCardArrowWrapper
                    cardType={props.cardType}
                    hasPadding={props.hasPadding}
                    topic={topicName}
                    to={toLinkWithHint(props.item)}
                >
                    <StyledCardFold
                        cardType={props.cardType}
                        hasPadding={props.hasPadding}
                        topic={topicName}
                    />
                    <ArrowIcon />
                </StyledCardArrowWrapper>
            </StyledSNEnt>
        )
    }
    return null
}
SNEntCard.displayName = 'SNEntCard'

export function canPlayRestrictedVideo(
    restrictions: VideoPlaybackRestrictionTypes,
    isEntitled: boolean,
): boolean {
    switch (restrictions) {
        case 'all':
            return true
        case 'registered':
            return true
        case 'entitled':
            return isEntitled
        default:
            return false
    }
}
